import type { ZodType } from 'zod';
import type {
  IChatApiRequest,
  IChatApiHeaders,
  IChatStreamCallback,
  IChatJsonCallback,
  IChatErrorEnvelope,
  IChatRequestOptions,
} from '../types/ChatApi.types';
import appointmentSlotsMock from '../mockResponses/appointmentSlots.json';
import captureUserDetailsMock from '../mockResponses/captureUserDetails.json';
import providerResultsMock from '../mockResponses/providerResults.json';
import reviewAppointmentMock from '../mockResponses/reviewAppointment.json';
import { getChatApiUrl } from './ChatApiEndpoint';

const EVENT_DELIMITER = /\r?\n\r?\n/;
const LINE_DELIMITER = /\r?\n/;

type MockResponseKey =
  | 'providerResults'
  | 'appointmentSlots'
  | 'captureUserDetails'
  | 'reviewAppointment';

const MOCK_RESPONSES: Record<MockResponseKey, unknown> = {
  providerResults: providerResultsMock,
  appointmentSlots: appointmentSlotsMock,
  captureUserDetails: captureUserDetailsMock,
  reviewAppointment: reviewAppointmentMock,
};

const getMockResponseKey = (request: IChatApiRequest): MockResponseKey => {
  const requestedKey = request.variables?.mockJsonResponseKey;
  if (typeof requestedKey === 'string' && requestedKey in MOCK_RESPONSES) {
    return requestedKey as MockResponseKey;
  }
  return 'providerResults';
};

const shouldUseMockJsonResponse = (request: IChatApiRequest): boolean => {
  const mockFlag = request.variables?.useMockJsonResponse;

  // In development we default to mocks until backend JSON is fully available.
  if (typeof mockFlag === 'boolean') {
    return mockFlag;
  }

  return false;
};

/**
 * Typed error thrown for any non-2xx chat response (or schema mismatch),
 * mirroring `ApiError` in the shared `hs-mp-web` API client (`src/api/client.ts`).
 */
export class ChatApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly correlationId: string | undefined;
  readonly details: Record<string, unknown> | undefined;

  constructor(status: number, envelope: IChatErrorEnvelope) {
    super(envelope.message);
    this.name = 'ChatApiError';
    this.status = status;
    this.code = envelope.code;
    this.correlationId = envelope.correlation_id;
    this.details = envelope.details;
  }
}

const safeParseError = async (response: Response): Promise<IChatErrorEnvelope> => {
  const fallbackMessage = `Chat API request failed: ${response.status} ${response.statusText}`;

  try {
    const body = (await response.json()) as Partial<IChatErrorEnvelope>;

    if (body && typeof body.code === 'string' && typeof body.message === 'string') {
      return body as IChatErrorEnvelope;
    }

    if (body && typeof body.message === 'string') {
      return {
        code: response.status === 401 ? 'UNAUTHENTICATED' : 'INTERNAL_ERROR',
        message: body.message,
      };
    }
  } catch {
    // Fall through to the generic envelope below.
  }

  return {
    code: response.status === 401 ? 'UNAUTHENTICATED' : 'INTERNAL_ERROR',
    message: fallbackMessage,
  };
};

const validateJsonResponse = (
  value: unknown,
  schema: ZodType<unknown> | undefined,
  correlationId: string,
): unknown => {
  if (!schema) {
    return value;
  }

  const result = schema.safeParse(value);

  if (!result.success) {
    throw new ChatApiError(200, {
      code: 'SCHEMA_VALIDATION_FAILED',
      message: 'Chat API response did not match the expected schema',
      correlation_id: correlationId,
      details: { issues: result.error.issues },
    });
  }

  return result.data;
};

interface ISseProcessingState {
  pendingJsonText: string;
  hasSeenJsonStart: boolean;
}

const tryParseJsonValue = (value: string): unknown | null => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const tryEmitBufferedJson = (
  state: ISseProcessingState,
  onJsonResponse: IChatJsonCallback,
): boolean => {
  const trimmed = state.pendingJsonText.trim();

  if (!trimmed) {
    return false;
  }

  const parsed = tryParseJsonValue(trimmed);

  if (parsed !== null && (typeof parsed === 'object' || Array.isArray(parsed))) {
    onJsonResponse(parsed);
    state.pendingJsonText = '';
    state.hasSeenJsonStart = false;
    return true;
  }

  return false;
};

const handlePotentialJsonChunk = (
  chunk: string,
  state: ISseProcessingState,
  onTextChunk: IChatStreamCallback,
  onJsonResponse: IChatJsonCallback,
): void => {
  if (!chunk) {
    return;
  }

  const trimmedStart = chunk.trimStart();
  const looksLikeJsonStart = trimmedStart.startsWith('{') || trimmedStart.startsWith('[');

  if (state.hasSeenJsonStart || looksLikeJsonStart) {
    state.pendingJsonText += chunk;
    state.hasSeenJsonStart = true;

    if (tryEmitBufferedJson(state, onJsonResponse)) {
      return;
    }

    // Keep buffering until we have a complete JSON value.
    return;
  }

  onTextChunk(chunk);
};

const handleSseEvent = (
  eventText: string,
  state: ISseProcessingState,
  onTextChunk: IChatStreamCallback,
  onJsonResponse: IChatJsonCallback,
): void => {
  const lines = eventText.split(LINE_DELIMITER).map((line) => line.trim());

  // Parse SSE event format (matches OptumusMessagingController response)
  // event: start|message|done|error
  // data: <json-payload>
  let eventType = 'message'; // default
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith('event:')) {
      eventType = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    }
  }

  // Handle different event types
  if (eventType === 'start') {
    // Start event: { conversationId, timestamp }
    return;
  }

  if (eventType === 'done') {
    // Done event: { conversationId, timestamp }
    // Flush any buffered JSON
    if (state.pendingJsonText.trim()) {
      tryEmitBufferedJson(state, onJsonResponse);
    }
    return;
  }

  if (eventType === 'error') {
    // Error event: { error: string }. Surface it so the caller stops the
    // loading state and shows an error instead of streaming forever.
    let message = "We're unable to process your request at this time. Please try again later.";
    if (dataLines.length > 0) {
      const parsed = tryParseJsonValue(dataLines[0]);
      if (parsed && typeof parsed === 'object' && 'error' in parsed) {
        const parsedError = (parsed as { error?: unknown }).error;
        if (typeof parsedError === 'string' && parsedError.trim()) {
          message = parsedError;
        }
      } else if (typeof parsed === 'string' && parsed.trim()) {
        message = parsed;
      }
    }
    throw new ChatApiError(200, {
      code: 'CONVERSATION_ERROR',
      message,
    });
  }

  // Handle message events (the actual chat content)
  for (const dataContent of dataLines) {
    if (!dataContent) continue;

    if (dataContent === '[DONE]') {
      continue;
    }

    // WORKAROUND: Backend currently sends double-encoded data like: data: "escaped-json"
    // Remove the extra "data: " prefix if present
    const cleanedContent = dataContent.startsWith('data: ')
      ? dataContent.substring(6).trim()
      : dataContent;

    const parsed = tryParseJsonValue(cleanedContent);

    if (parsed !== null && typeof parsed === 'object') {
      onJsonResponse(parsed);
      continue;
    }

    // If parsed is a string, it might be double-encoded JSON - try parsing again
    if (typeof parsed === 'string') {
      const doubleParseAttempt = tryParseJsonValue(parsed);
      if (doubleParseAttempt !== null && typeof doubleParseAttempt === 'object') {
        onJsonResponse(doubleParseAttempt);
        continue;
      }
      handlePotentialJsonChunk(parsed, state, onTextChunk, onJsonResponse);
      continue;
    }

    handlePotentialJsonChunk(
      cleanedContent.replace(/^"|"$/g, ''),
      state,
      onTextChunk,
      onJsonResponse,
    );
  }
};

const readSseBody = async (
  response: Response,
  onTextChunk: IChatStreamCallback,
  onJsonResponse: IChatJsonCallback,
): Promise<void> => {
  const reader = response.body?.getReader?.();
  const state: ISseProcessingState = {
    pendingJsonText: '',
    hasSeenJsonStart: false,
  };

  if (reader) {
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      let readResult = await reader.read();

      while (!readResult.done) {
        const { value } = readResult;

        buffer += decoder.decode(value, { stream: true });

        const events = buffer.split(EVENT_DELIMITER);
        buffer = events.pop() ?? '';

        for (const eventText of events) {
          handleSseEvent(eventText, state, onTextChunk, onJsonResponse);
        }

        readResult = await reader.read();
      }

      const remainingText = `${buffer}${decoder.decode()}`.trim();

      if (remainingText) {
        handleSseEvent(remainingText, state, onTextChunk, onJsonResponse);
      }

      if (state.pendingJsonText.trim()) {
        if (!tryEmitBufferedJson(state, onJsonResponse)) {
          onTextChunk(state.pendingJsonText);
        }
      }
    } finally {
      reader.releaseLock();
    }

    return;
  }

  const text = await response.text();
  const events = text.split(EVENT_DELIMITER);

  for (const eventText of events) {
    if (eventText.trim()) {
      handleSseEvent(eventText, state, onTextChunk, onJsonResponse);
    }
  }

  if (state.pendingJsonText.trim()) {
    if (!tryEmitBufferedJson(state, onJsonResponse)) {
      onTextChunk(state.pendingJsonText);
    }
  }
};

export const streamChatResponse = async (
  request: IChatApiRequest,
  headers: IChatApiHeaders,
  onTextChunk: IChatStreamCallback,
  onJsonResponse: IChatJsonCallback,
  options: IChatRequestOptions = {},
): Promise<void> => {
  const url = getChatApiUrl();
  const correlationId = options.correlationId ?? crypto.randomUUID();
  const healthExToken =
    headers.healthExToken && typeof headers.healthExToken === 'string' ? headers.healthExToken : '';

  const healthExHeaders: Record<string, string> = healthExToken
    ? {
        'x-healthex-token': healthExToken,
        'x-healthex-connected': 'true',
      }
    : {};

  // Get CSRF token from cookie for Spring Security CSRF protection
  const getCsrfToken = (): string => {
    if (typeof document === 'undefined') {
      return '';
    }
    const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/);
    return match ? decodeURIComponent(match[1]) : '';
  };

  const csrfToken = getCsrfToken();

  // BFF endpoint (/secure/api/conversation) expects { prompt: string }
  // and authenticates via hs_session cookie. The backend derives session-id, user-id, e-id
  // from the session, so we don't need to send them as headers (they're ignored anyway).
  // Location headers are optional and will be forwarded if present.
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    'optum-cid-ext': correlationId,
    ...healthExHeaders,
  };

  // Add CSRF token header (required by backend for /secure/api/** endpoints)
  // The XSRF-TOKEN cookie is set by the server and must be echoed in the X-XSRF-TOKEN header
  if (csrfToken) {
    requestHeaders['X-XSRF-TOKEN'] = csrfToken;
  } else {
    console.warn(
      'XSRF-TOKEN cookie not found. CSRF protection may fail. ' +
        'Make sure to call a GET endpoint first (e.g., /secure/api/sessionDetails) to obtain the token.',
    );
  }

  if (headers.location.latitude && headers.location.longitude) {
    requestHeaders['location-lat'] = headers.location.latitude;
    requestHeaders['location-lng'] = headers.location.longitude;
  }

  // Request body matches ConversationPromptRequest.java: { prompt: string }
  const requestBody = {
    prompt: request.query,
  };

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include', // Sends hs_session cookie for authentication
    headers: requestHeaders,
    body: JSON.stringify(requestBody),
    signal: options.signal,
  });

  if (!response.ok) {
    throw new ChatApiError(response.status, await safeParseError(response));
  }

  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json')) {
    const jsonResponse = (await response.json()) as unknown;
    onJsonResponse(validateJsonResponse(jsonResponse, options.schema, correlationId));
    return;
  }

  if (shouldUseMockJsonResponse(request)) {
    const mockKey = getMockResponseKey(request);
    onJsonResponse(MOCK_RESPONSES[mockKey]);
    return;
  }

  await readSseBody(response, onTextChunk, onJsonResponse);
};
